import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  LayoutRightIcon,
  Maximize01Icon,
  Minimize01Icon,
  Settings01Icon,
} from '@langchain/untitled-ui-icons';
import { Checkbox } from '@mui/joy';

import { ReactNode } from 'react';

import Header from '@/components/CodeHeader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/Dropdown';
import { IconButton, Text, Tooltip } from '@/design-system';
import SlidersIcon2 from '@/icons/SlidersIcon2.svg?react';
import { RunSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { useNodeAppearanceSettings } from './useNodeAppearanceStore';

export function ToggleButton(props: {
  isActive: boolean;
  onClick: () => void;
  text: ReactNode;
  activeClass?: string;
  activeIcon?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  tooltip?: string;
}) {
  return (
    <Tooltip title={props.tooltip}>
      <button
        type="button"
        className={cn(
          'flex items-center gap-1.5 rounded-md border border-secondary px-2 py-1.5 text-xs font-medium outline-none',
          props.isActive && props.activeClass,
          props.className
        )}
        disabled={props.disabled}
        onClick={props.onClick}
      >
        <div className="flex flex-row">
          <div className={props.isActive && props.activeIcon ? 'pr-1' : ''}>
            {props.text}
          </div>
          {props.isActive && props.activeIcon && (
            <div className="my-auto w-4">{props.activeIcon}</div>
          )}
        </div>
      </button>
    </Tooltip>
  );
}

function RelevanceOption(props: {
  value: 'filtered' | 'most_relevant' | 'all';
  onChange: (value: 'filtered' | 'most_relevant' | 'all') => void;
  disabled?: boolean;

  type: 'most_relevant' | 'all' | 'filtered';
  className?: string;
}) {
  const isActive = props.type === props.value;

  return (
    <ToggleButton
      onClick={() => props.onChange(props.type)}
      className={cn(
        'w-full hover:bg-secondary-hover active:bg-secondary disabled:opacity-50 data-[state=open]:bg-secondary',
        props.className
      )}
      disabled={props.disabled}
      text={
        props.type === 'most_relevant'
          ? 'Most relevant'
          : props.type === 'filtered'
          ? 'Filtered Only'
          : 'Show All'
      }
      isActive={isActive}
      activeIcon={<CheckIcon />}
    />
  );
}

function CheckboxOption(props: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex w-full items-center gap-2 px-4 py-1.5 text-xs ',
        props.className
      )}
    >
      <Checkbox
        size="sm"
        checked={props.checked}
        onChange={(e) => props.onChange(e.target.checked)}
      />
      <span>{props.label}</span>
    </div>
  );
}

function NodeAppearanceDropdown(props: {
  visibility: 'filtered' | 'most_relevant' | 'all';
  setVisibility: (value: 'filtered' | 'most_relevant' | 'all') => void;
  hideFilteredVisibility?: boolean;
  disableFiltering?: boolean;
  hiddenCount: number;
  filterCount: number;
  isPublicPage: boolean;
}) {
  const {
    showLatency,
    setShowLatency,
    showTokens,
    setShowTokens,
    showTags,
    setShowTags,
  } = useNodeAppearanceSettings(props.isPublicPage);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          icon={Settings01Icon}
          label="Tree settings"
          variant="outlined"
          color="secondary"
          size="sm"
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent className="p-0">
        <Text
          variant="h4"
          className="w-full bg-tertiary px-4 py-1 text-secondary"
        >
          Display
        </Text>

        <CheckboxOption
          checked={showLatency}
          onChange={setShowLatency}
          label="Show Latency"
        />

        <CheckboxOption
          checked={showTokens}
          onChange={setShowTokens}
          label="Show Token Counts"
        />

        <CheckboxOption
          checked={showTags}
          onChange={setShowTags}
          label="Show Tags"
        />
        <Text
          variant="h4"
          className="w-full bg-tertiary px-4 py-1 text-secondary"
        >
          Visibility
        </Text>

        <RelevanceOption
          type={'most_relevant'}
          value={props.visibility}
          onChange={props.setVisibility}
          disabled={props.hiddenCount === 0 || props.disableFiltering}
          className="border-none px-4"
        />

        {!props.hideFilteredVisibility && (
          <RelevanceOption
            type={'filtered'}
            value={props.visibility}
            onChange={props.setVisibility}
            disabled={props.filterCount === 0 || props.disableFiltering}
            className="border-none px-4"
          />
        )}

        <RelevanceOption
          type={'all'}
          value={props.visibility}
          onChange={props.setVisibility}
          disabled={props.hiddenCount === 0 || props.disableFiltering}
          className="border-none px-4"
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FiltersDropdown(props: {
  visibility: 'filtered' | 'most_relevant' | 'all';
  setVisibility: (value: 'filtered' | 'most_relevant' | 'all') => void;
  hideFilteredVisibility?: boolean;
  disableFiltering?: boolean;
  childRunLookup: Record<string, RunSchema[] | undefined>;

  hiddenCount: number;
  filterCount: number;

  additionalFilters?: ReactNode;

  foldedRuns: string[];
  toggleFoldAllRuns?: () => void;
  isPublicPage: boolean;
}) {
  return (
    <DropdownMenu>
      <div className="flex flex-wrap items-center gap-1">
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'py-1/2 flex items-center gap-1 whitespace-nowrap rounded-md border border-secondary px-1 text-left text-xs outline-none transition-colors',
              'hover:bg-secondary-hover data-[state=open]:bg-secondary'
            )}
          >
            <div className="scale-[0.6]">
              <SlidersIcon2 />
            </div>
            <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-tertiary" />
          </button>
        </DropdownMenuTrigger>
      </div>

      <DropdownMenuContent>
        <RelevanceOption
          type={'most_relevant'}
          value={props.visibility}
          onChange={props.setVisibility}
          disabled={props.hiddenCount === 0 || props.disableFiltering}
          className="border-none"
        />

        {!props.hideFilteredVisibility && (
          <RelevanceOption
            type={'filtered'}
            value={props.visibility}
            onChange={props.setVisibility}
            disabled={props.filterCount === 0 || props.disableFiltering}
            className="border-none"
          />
        )}

        <RelevanceOption
          type={'all'}
          value={props.visibility}
          onChange={props.setVisibility}
          disabled={props.hiddenCount === 0 || props.disableFiltering}
          className="border-none"
        />

        <DropdownMenuSeparator />

        {props.toggleFoldAllRuns && (
          <ToggleButton
            onClick={props.toggleFoldAllRuns}
            isActive={props.foldedRuns.length > 0}
            activeIcon={<CheckIcon />}
            className="w-full border-none hover:bg-secondary-hover"
            text="Collapse"
          />
        )}
        {props.additionalFilters}
      </DropdownMenuContent>
      <NodeAppearanceDropdown
        visibility={props.visibility}
        setVisibility={props.setVisibility}
        hideFilteredVisibility={props.hideFilteredVisibility}
        disableFiltering={props.disableFiltering}
        hiddenCount={props.hiddenCount}
        filterCount={props.filterCount}
        isPublicPage={props.isPublicPage}
      />
    </DropdownMenu>
  );
}

function FiltersRow(props: {
  visibility: 'filtered' | 'most_relevant' | 'all';
  setVisibility: (value: 'filtered' | 'most_relevant' | 'all') => void;
  hideFilteredVisibility?: boolean;
  disableFiltering?: boolean;

  childRunLookup: Record<string, RunSchema[] | undefined>;

  hiddenCount: number;
  filterCount: number;

  foldedRuns: string[];
  toggleFoldAllRuns?: () => void;

  additionalFilters?: ReactNode;
  right?: ReactNode;
  isPublicPage: boolean;
}) {
  return (
    <div className="flex w-full flex-nowrap items-center justify-between gap-1">
      <div className="flex items-center gap-1">
        <div className="flex gap-1">{props.additionalFilters}</div>
        <NodeAppearanceDropdown
          visibility={props.visibility}
          setVisibility={props.setVisibility}
          hideFilteredVisibility={props.hideFilteredVisibility}
          disableFiltering={props.disableFiltering}
          hiddenCount={props.hiddenCount}
          filterCount={props.filterCount}
          isPublicPage={props.isPublicPage}
        />
        {props.toggleFoldAllRuns && (
          <IconButton
            icon={props.foldedRuns.length > 0 ? Maximize01Icon : Minimize01Icon}
            onClick={props.toggleFoldAllRuns}
            variant="outlined"
            color="secondary"
            size="sm"
            label={
              props.foldedRuns.length > 0 ? 'Expand runs' : 'Collapse runs'
            }
          />
        )}
      </div>
      {props.right}
    </div>
  );
}

function Filters(props: {
  isXsPanel: boolean;

  hiddenCount: number;
  filterCount: number;

  visibility: 'filtered' | 'most_relevant' | 'all';
  setVisibility: (value: 'filtered' | 'most_relevant' | 'all') => void;
  hideFilteredVisibility?: boolean;
  disableFiltering?: boolean;

  childRunLookup: Record<string, RunSchema[] | undefined>;

  foldedRuns: string[];
  toggleFoldAllRuns?: () => void;

  insidePane?: boolean;
  additionalFilters?: ReactNode;
  isPublicPage: boolean;
}) {
  return props.isXsPanel ? (
    <FiltersDropdown {...props} />
  ) : (
    <FiltersRow {...props} />
  );
}

function RunDetailsNavigationHeader(props: {
  isXsPanel: boolean;

  hiddenCount: number;
  filterCount: number;
  additionalFilters?: ReactNode;
  right?: ReactNode;

  visibility: 'filtered' | 'most_relevant' | 'all';
  setVisibility: (value: 'filtered' | 'most_relevant' | 'all') => void;
  hideFilteredVisibility?: boolean;
  disableFiltering?: boolean;
  foldedRuns: string[];
  toggleFoldAllRuns?: () => void;
  childRunLookup: Record<string, RunSchema[] | undefined>;

  togglePanelSize?: () => void;
  insidePane?: boolean;
  comparing?: boolean;
  isPublicPage: boolean;
  headerLabel?: string;
}) {
  return (
    <Header
      sx={{
        mx: '16px',
        mt: '16px',
        userSelect: 'none',
        flexShrink: 0,
        flexDirection: 'column',
        height: 'auto',
      }}
      text={
        <div className="my-auto flex h-full items-center justify-between">
          <Text variant="h4">{props.headerLabel ?? 'Trace'}</Text>
          {!props.comparing && !!props.togglePanelSize && (
            <IconButton
              onClick={props.togglePanelSize}
              icon={LayoutRightIcon}
              label="Toggle panel size"
              variant="plain"
              color="secondary"
              size="sm"
              tooltipProps={{
                side: 'left',
              }}
            />
          )}
        </div>
      }
      textSx={{ mr: 'auto', my: 'auto', height: '32px', width: '100%' }}
      rightSx={{ mr: 'auto', width: '100%' }}
      right={<Filters {...props} isPublicPage={props.isPublicPage} />}
    />
  );
}

export default RunDetailsNavigationHeader;
